/**
 * Admin Dashboard Page - 관리자 대시보드
 *
 * 시스템 전체 현황을 한눈에 볼 수 있는 대시보드입니다.
 * - 워크플로우 / 지식 파이프라인 / 문서 / 챗봇 현황
 * - 최근 활동 · 최근 워크플로우 · 최근 지식 파이프라인 (3단, 데이터 없으면 빈 상태)
 * - 빠른 실행
 */

import { useState, useEffect, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Topbar } from '../components/layout';
import { Badge, Button, useToast } from '../components/ui';
import type { BadgeColor } from '../components/ui';
import { workflowApi, knowledgeBaseApi, Workflow, KnowledgeBase } from '../api';
import { authApi } from '../api';
import { logsApi, type FailureSummary } from '../api/logs';
import { getKbManagePath } from './KnowledgeBasesPage';
import WorkflowIcon from '../assets/icons/dashboard/workflow.svg?react';
import PipelineIcon from '../assets/icons/dashboard/pipeline.svg?react';
import ChatIcon from '../assets/icons/dashboard/chat.svg?react';
import CalendarIcon from '../assets/icons/dashboard/calendar.svg?react';
import MailIcon from '../assets/icons/dashboard/mail.svg?react';
import TemplateIcon from '../assets/icons/dashboard/template.svg?react';
import ClockIcon from '../assets/icons/dashboard/clock.svg?react';
import MarketplaceIcon from '../assets/icons/dashboard/marketplace.svg?react';
// 실패 경고 배너
import WarningIcon from '../assets/icons/common/warning.svg?react';
import ChevronRightIcon from '../assets/icons/common/chevron-right.svg?react';
import DocumentIcon from '../assets/icons/dashboard/document.svg?react';
import PlusIcon from '../assets/icons/common/plus.svg?react';
import RefreshIcon from '../assets/icons/common/refresh.svg?react';
import '../styles/adminPage.css';

interface DashboardStats {
  totalWorkflows: number;
  deployedWorkflows: number;
  adminWorkflows: number;
  userWorkflows: number;
  draftWorkflows: number;
  totalKnowledgeBases: number;
  activeKnowledgeBases: number;
  syncingKnowledgeBases: number;
  errorKnowledgeBases: number;
  totalDocuments: number;
  activeChatbots: number;
  adminChatbots: number;
  userChatbots: number;
  activeSchedules: number;
  schedulesToday: number;
  schedulesWeek: number;
  schedulesLater: number;
}

interface RecentActivity {
  id: string;
  type: 'workflow' | 'kb';
  title: string;
  description: string;
  timestamp: Date;
  link: string; // 클릭 시 이동 (워크플로우 = 빌더, 지식 파이프라인 = 해당 소스 페이지 + 관리 모달)
  workflow?: Workflow; // 워크플로우 활동이면 종류 아이콘(챗봇 · 스케줄 · 메일)용
}

// 이 트리거가 있으면 자동 실행 워크플로우 → 챗봇 아님 (ChatPage 챗봇 목록과 같은 기준)
const NON_CHAT_TRIGGERS = ['schedule-trigger', 'mail-trigger', 'webhook-trigger', 'google-drive', 'notion', 'gmail', 'naver'];

// 워크플로우 종류 아이콘 (최근 워크플로우 표): 스케줄 = 캘린더, 메일 = 봉투, 그 외 대화형 = 챗봇
const MAIL_TRIGGERS = ['mail-trigger', 'gmail', 'naver'];
const renderWorkflowTypeIcon = (workflow: Workflow) => {
  const types = workflow.nodes?.map(n => n.type) ?? [];
  if (types.includes('schedule-trigger')) return <CalendarIcon />;
  if (types.some(t => MAIL_TRIGGERS.includes(t))) return <MailIcon />;
  if (!types.some(t => NON_CHAT_TRIGGERS.includes(t))) return <ChatIcon />;
  return <WorkflowIcon />; // 웹훅 · Drive · Notion 등 그 밖의 자동 실행
};

const EMPTY_STATS: DashboardStats = {
  totalWorkflows: 0,
  deployedWorkflows: 0,
  adminWorkflows: 0,
  userWorkflows: 0,
  draftWorkflows: 0,
  totalKnowledgeBases: 0,
  activeKnowledgeBases: 0,
  syncingKnowledgeBases: 0,
  errorKnowledgeBases: 0,
  totalDocuments: 0,
  activeChatbots: 0,
  adminChatbots: 0,
  userChatbots: 0,
  activeSchedules: 0,
  schedulesToday: 0,
  schedulesWeek: 0,
  schedulesLater: 0,
};

// 스케줄 다음 실행 시각 계산 헬퍼 (UserHomePage.getNextRun 과 같은 로직)
const _DAY_MAP: Record<string, number> = { sunday:0, monday:1, tuesday:2, wednesday:3, thursday:4, friday:5, saturday:6 };
const _getWeeklyDay = (raw: unknown) => typeof raw === 'number' ? raw : _DAY_MAP[String(raw ?? '').toLowerCase()] ?? (Number(raw) || 1);
const _getScheduleData = (wf: Workflow) => { const n = wf.nodes.find(n => n.type === 'schedule-trigger'); return n ? (n.data as Record<string, unknown>) : null; };
const _daysFrom = (d: Date, now: Date) => { const s = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime(); return Math.round((s(d) - s(now)) / 86400000); };
const _getNextRun = (wf: Workflow, now: Date): Date | null => {
  const d = _getScheduleData(wf);
  if (!d) return null;
  const type = (d.scheduleType as string) || 'daily';
  const next = new Date(now);
  if (type === 'cron') return null;
  if (type === 'interval') { const ms = Math.max(Number(d.interval) || 5, 1) * 60000; let t = now.getTime() + ms; return new Date(t); }
  if (type === 'hourly') { next.setMinutes(Number(d.hourlyMinute ?? 0), 0, 0); if (next <= now) next.setHours(next.getHours() + 1); return next; }
  if (type === 'weekly') {
    next.setHours(Number(d.weeklyHour ?? 9), Number(d.weeklyMinute ?? 0), 0, 0);
    let du = _getWeeklyDay(d.weeklyDay) - now.getDay();
    if (du < 0 || (du === 0 && next <= now)) du += 7;
    next.setDate(next.getDate() + du);
    return next;
  }
  next.setHours(Number(d.dailyHour ?? 9), Number(d.dailyMinute ?? 0), 0, 0);
  if (next <= now) next.setDate(next.getDate() + 1);
  return next;
};

// 상태 → 배지 색 · 라벨
const WORKFLOW_STATUS: Record<Workflow['status'], { color: BadgeColor; label: string }> = {
  deployed: { color: 'success', label: '배포됨' },
  draft: { color: 'medium', label: '초안' },
  archived: { color: 'gray', label: '보관됨' },
};

const KB_STATUS: Record<KnowledgeBase['status'], { color: BadgeColor; label: string }> = {
  active: { color: 'info', label: '활성' },
  syncing: { color: 'primary', label: '동기화 중' },
  indexing: { color: 'primary', label: '색인 중' },
  error: { color: 'error', label: '오류' },
  'needs-sync': { color: 'medium', label: '동기화 필요' },
};

const formatUpdatedAt = (d: Date) =>
  `${d.toLocaleDateString('ko-KR')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')} 기준`;

const formatDate = (date: Date | string) => {
  const d = typeof date === 'string' ? new Date(date) : date;
  const diff = Date.now() - d.getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return '방금 전';
  if (minutes < 60) return `${minutes}분 전`;
  if (hours < 24) return `${hours}시간 전`;
  if (days < 7) return `${days}일 전`;
  return d.toLocaleDateString('ko-KR');
};

// 최근 활동을 오늘 / 이번 주 / 이전 으로 묶음
const groupActivities = (list: RecentActivity[]) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const weekAgo = today.getTime() - 6 * 86400000;

  const groups: { label: string; items: RecentActivity[] }[] = [
    { label: '오늘', items: [] },
    { label: '이번 주', items: [] },
    { label: '이전', items: [] },
  ];
  list.forEach(a => {
    const t = a.timestamp.getTime();
    if (t >= today.getTime()) groups[0].items.push(a);
    else if (t >= weekAgo) groups[1].items.push(a);
    else groups[2].items.push(a);
  });
  return groups.filter(g => g.items.length > 0);
};

// 카드 안 빈 상태
function DashEmpty({ icon, title, desc, actions }: { icon: ReactNode; title: string; desc: string; actions?: ReactNode }) {
  return (
    <div className="dash-empty">
      <span className="item-card-icon item-card-icon--primary">{icon}</span>
      <b>{title}</b>
      <p>{desc}</p>
      {actions && <div className="dash-empty-actions">{actions}</div>}
    </div>
  );
}

export function AdminDashboardPage() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const currentUser = authApi.getUser();
  const role = currentUser?.role ?? '';

  const scopeDesc = role === 'productAdmin' ? '전체 시스템 현황을 확인하세요'
    : role === 'tenantAdmin'   ? '본인 테넌트 하위 현황을 확인하세요'
    : role === 'customerAdmin' ? '본인 고객사 하위 현황을 확인하세요'
    : role === 'teamAdmin'     ? '본인 팀 하위 현황을 확인하세요'
    : '현황을 확인하세요';
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [loadedAt, setLoadedAt] = useState<Date | null>(null);
  const [stats, setStats] = useState<DashboardStats>(EMPTY_STATS);
  const [recentWorkflows, setRecentWorkflows] = useState<Workflow[]>([]);
  const [recentKnowledgeBases, setRecentKnowledgeBases] = useState<KnowledgeBase[]>([]);
  const [activities, setActivities] = useState<RecentActivity[]>([]);
  const [failureSummary, setFailureSummary] = useState<FailureSummary>({ count: 0, recentNames: [] });

  useEffect(() => {
    loadDashboardData().finally(() => setLoading(false));
  }, []);

  const loadDashboardData = async () => {
    try {
      // 워크플로우 데이터 로드
      const workflows = await workflowApi.list({ limit: 500 });

      // 지식 파이프라인 데이터 로드
      const knowledgeBases = await knowledgeBaseApi.list({ includeSystem: true });

      // 활성 챗봇 = 배포된 대화형 워크플로우 전체 (챗봇 미리보기 목록과 같은 기준)
      // 관리자 = 관리자 등록(회사 공식), 사용자 = 그 외 (챗봇 미리보기 탭과 같은 구분)
      const chatbots = workflows.filter(w =>
        w.status === 'deployed' && !w.nodes?.some(n => NON_CHAT_TRIGGERS.includes(n.type))
      );
      const adminChatbots = chatbots.filter(w => w.isSharedAsChatbot).length;

      const deployedWfs = workflows.filter(w => w.status === 'deployed');
      const adminWfs = deployedWfs.filter(w => w.isSharedAsChatbot || w.isSharedAsTemplate).length;

      setStats({
        totalWorkflows: workflows.length,
        deployedWorkflows: deployedWfs.length,
        adminWorkflows: adminWfs,
        userWorkflows: deployedWfs.length - adminWfs,
        draftWorkflows: workflows.filter(w => w.status === 'draft').length,
        totalKnowledgeBases: knowledgeBases.length,
        activeKnowledgeBases: knowledgeBases.filter(kb => kb.status === 'active').length,
        syncingKnowledgeBases: knowledgeBases.filter(kb => kb.status === 'syncing' || kb.status === 'indexing').length,
        errorKnowledgeBases: knowledgeBases.filter(kb => kb.status === 'error' || kb.status === 'needs-sync').length,
        totalDocuments: knowledgeBases.reduce((sum, kb) => sum + (kb.documentCount || 0), 0),
        activeChatbots: chatbots.length,
        adminChatbots,
        userChatbots: chatbots.length - adminChatbots,
        activeSchedules: 0, schedulesToday: 0, schedulesWeek: 0, schedulesLater: 0,
      });

      // 활성 스케줄 통계 (비동기 추가 로드)
      try {
        const activeIds = new Set(await workflowApi.getActiveSchedules());
        const now = new Date();
        const scheduleWfs = workflows.filter(w =>
          w.nodes.some(n => n.type === 'schedule-trigger') &&
          activeIds.has(w.id)
        );
        const buckets = { today: 0, week: 0, later: 0 };
        scheduleWfs.forEach(w => {
          const next = _getNextRun(w, now);
          const days = next ? _daysFrom(next, now) : Infinity;
          if (days <= 0) buckets.today++;
          else if (days < 7) buckets.week++;
          else buckets.later++;
        });
        setStats(prev => ({ ...prev, activeSchedules: scheduleWfs.length, schedulesToday: buckets.today, schedulesWeek: buckets.week, schedulesLater: buckets.later }));
      } catch { /* 스케줄 조회 실패 무시 */ }

      // 최근 워크플로우 (최근 8개)
      const sortedWorkflows = [...workflows].sort(
        (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
      );
      setRecentWorkflows(sortedWorkflows.slice(0, 8));

      // 최근 지식 파이프라인 (최근 8개)
      const sortedKbs = [...knowledgeBases].sort(
        (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
      );
      setRecentKnowledgeBases(sortedKbs.slice(0, 8));

      // 최근 활동 생성
      const recentActivities: RecentActivity[] = [
        ...sortedWorkflows.slice(0, 3).map(w => ({
          id: `wf-${w.id}`,
          type: 'workflow' as const,
          title: w.name,
          description: w.status === 'deployed' ? '워크플로우가 배포되었습니다' : '워크플로우가 수정되었습니다',
          timestamp: new Date(w.updatedAt),
          link: `/workflows/${w.id}`,
          workflow: w,
        })),
        ...sortedKbs.slice(0, 3).map(kb => ({
          id: `kb-${kb.id}`,
          type: 'kb' as const,
          title: kb.name,
          description: `문서 ${kb.documentCount || 0}개 포함`,
          timestamp: new Date(kb.updatedAt),
          link: getKbManagePath(kb),
        })),
      ];

      // 시간순 정렬
      recentActivities.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
      setActivities(recentActivities.slice(0, 10));
      setLoadedAt(new Date());

      // 최근 24시간 실패 요약 (배너용) — 실패해도 대시보드 로드는 계속
      try {
        const summary = await logsApi.getFailuresSummary();
        setFailureSummary(summary);
      } catch {
        // 실패 요약 조회 실패는 조용히 무시
      }
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      showToast('error', '대시보드 데이터를 불러오지 못했습니다.');
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadDashboardData();
    setRefreshing(false);
  };

  // 워크플로우 목록의 "새 워크플로우" 와 같은 동작: 바로 생성 후 빌더로 이동
  const handleCreateWorkflow = async () => {
    if (creating) return;
    try {
      setCreating(true);
      const workflow = await workflowApi.create({ name: '새 워크플로우', description: '' });
      navigate(`/workflows/${workflow.id}`);
    } catch {
      showToast('error', '워크플로우 생성에 실패했습니다.');
      setCreating(false);
    }
  };

  const renderStatus = (status: { color: BadgeColor; label: string } | undefined, raw: string, pulse = false) => (
    <span className={`dash-status${pulse ? ' is-pulse' : ''}`}>
      <Badge color={status?.color ?? 'gray'}>{status?.label ?? raw}</Badge>
    </span>
  );

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="dashboard-loading-spinner" />
        <p>대시보드 로딩 중...</p>
      </div>
    );
  }

  const activityGroups = groupActivities(activities);

  return (
    <>
      <Topbar
        title="관리자 대시보드"
        description={scopeDesc}
        actions={
          <div className="dash-updated">
            {loadedAt && <span>{formatUpdatedAt(loadedAt)}</span>}
            <Button color="gray" appearance="outline" size="md" onClick={handleRefresh} disabled={refreshing}>
              <RefreshIcon className={refreshing ? 'dash-spin' : undefined} />
              새로고침
            </Button>
          </div>
        }
      />

      <div className="page-content">
        <div className="dash">

          {/* 실패 경고 배너 — 최근 24시간 내 실패 건이 있을 때만 표시 */}
          {failureSummary.count > 0 && (
            <div className="dash-failure-banner" role="alert">
              <span className="dash-failure-banner-icon" aria-hidden="true"><WarningIcon /></span>
              <span className="dash-failure-banner-msg">
                최근 24시간 내 워크플로우 실패<strong>{failureSummary.count}건</strong>
                {failureSummary.recentNames.length > 0 && (
                  <span className="dash-failure-banner-names">
                    - 
                    {failureSummary.recentNames.map((name) => (
                      <> {name}</>
                    ))}
                    {failureSummary.count > failureSummary.recentNames.length && (
                      <span className="dash-failure-more">
                        외 {failureSummary.count - failureSummary.recentNames.length}건
                      </span>
                    )}
                  </span>
                )}
              </span>
              <button
                className="dash-failure-banner-link"
                onClick={() => navigate('/admin/logs/failures')}
              >
                자세히 보기 →
              </button>
            </div>
          )}

          {/* 통계 카드 - 공통 item-card (마켓플레이스 카드와 같은 구조: 왼쪽 아이콘 | 내용)
              순서: 사용자 대시보드와 맞춤 (1 워크플로우 · 2 챗봇 · 3~4 화면별) */}
          <section className="dash-stats">
            <button type="button" className="item-card dash-stat" onClick={() => navigate('/workflows')}>
              <span className="item-card-icon item-card-icon--primary"><WorkflowIcon /></span>
              <div className="item-card-body">
                <div className="dash-stat-label">전체 워크플로우</div>
                <div className="dash-stat-value">{stats.totalWorkflows}<small>개</small></div>
                <div className="dash-bar">
                  <i style={{ flex: stats.adminWorkflows, background: 'var(--success)' }} />
                  <i style={{ flex: stats.userWorkflows, background: 'var(--medium)' }} />
                </div>
                <div className="dash-legend">
                  <span style={{ '--c': 'var(--success)' } as React.CSSProperties}>관리자 <b>{stats.adminWorkflows}</b></span>
                  <span style={{ '--c': 'var(--medium)' } as React.CSSProperties}>사용자 <b>{stats.userWorkflows}</b></span>
                </div>
              </div>
            </button>

            <button type="button" className="item-card dash-stat" onClick={() => navigate('/chat')}>
              <span className="item-card-icon item-card-icon--primary"><ChatIcon /></span>
              <div className="item-card-body">
                <div className="dash-stat-label">배포된 워크플로우</div>
                <div className="dash-stat-value">{stats.deployedWorkflows}<small>개</small></div>
                {/* 관리자 = primary, 사용자 = info (대시보드 다른 막대와 같은 색만 사용) */}
                <div className="dash-bar">
                  <i style={{ flex: stats.adminWorkflows, background: 'var(--primary)' }} />
                  <i style={{ flex: stats.userWorkflows, background: 'var(--info)' }} />
                </div>
                <div className="dash-legend">
                  <span style={{ '--c': 'var(--primary)' } as React.CSSProperties}>관리자 공유 <b>{stats.adminWorkflows}</b></span>
                  <span style={{ '--c': 'var(--info)' } as React.CSSProperties}>미공유 <b>{stats.userWorkflows}</b></span>
                </div>
              </div>
            </button>

            <button type="button" className="item-card dash-stat" onClick={() => navigate('/knowledge-bases')}>
              <span className="item-card-icon item-card-icon--primary"><PipelineIcon /></span>
              <div className="item-card-body">
                <div className="dash-stat-label">지식 파이프라인</div>
                <div className="dash-stat-value">{stats.totalKnowledgeBases}<small>개</small></div>
                <div className="dash-bar">
                  <i style={{ flex: stats.activeKnowledgeBases, background: 'var(--info)' }} />
                  <i style={{ flex: stats.syncingKnowledgeBases, background: 'var(--primary)' }} />
                  <i style={{ flex: stats.errorKnowledgeBases, background: 'var(--error)' }} />
                </div>
                <div className="dash-legend">
                  <span style={{ '--c': 'var(--info)' } as React.CSSProperties}>활성 <b>{stats.activeKnowledgeBases}</b></span>
                  <span style={{ '--c': 'var(--primary)' } as React.CSSProperties}>동기화 <b>{stats.syncingKnowledgeBases}</b></span>
                  <span style={{ '--c': 'var(--error)' } as React.CSSProperties}>오류 <b>{stats.errorKnowledgeBases}</b></span>
                </div>
              </div>
            </button>

            <button type="button" className="item-card dash-stat" onClick={() => navigate('/workflows')}>
              <span className="item-card-icon item-card-icon--primary"><CalendarIcon /></span>
              <div className="item-card-body">
                <div className="dash-stat-label">실행 중인 스케줄</div>
                <div className="dash-stat-value">{stats.activeSchedules}<small>개</small></div>
                <div className="dash-bar">
                  <i style={{ flex: stats.schedulesToday, background: 'var(--primary)' }} />
                  <i style={{ flex: stats.schedulesWeek, background: 'var(--info)' }} />
                  <i style={{ flex: stats.schedulesLater, background: 'var(--gray)' }} />
                </div>
                <div className="dash-legend">
                  <span style={{ '--c': 'var(--primary)' } as React.CSSProperties}>오늘 <b>{stats.schedulesToday}</b></span>
                  <span style={{ '--c': 'var(--info)' } as React.CSSProperties}>이번 주 <b>{stats.schedulesWeek}</b></span>
                  <span style={{ '--c': 'var(--gray)' } as React.CSSProperties}>그 이후 <b>{stats.schedulesLater}</b></span>
                </div>
              </div>
            </button>
          </section>

          {/* 최근 활동 · 최근 워크플로우 · 최근 지식 파이프라인 */}
          <section className="dash-cols">
            {/* 최근 활동 */}
            <div className="dash-card">
              <div className="dash-card-head">
                <h2>최근 활동</h2>
              </div>
              {activityGroups.length === 0 ? (
                <DashEmpty
                  icon={<ClockIcon />}
                  title="아직 활동 내역이 없어요"
                  desc="워크플로우를 배포하거나 지식 파이프라인에 문서를 추가하면 여기에 표시됩니다."
                />
              ) : (
                <div className="dash-activity">
                  {activityGroups.map(group => (
                    <div key={group.label} className="dash-activity-group">
                      <p className="dash-activity-label">{group.label}<em>{group.items.length}</em></p>
                      {group.items.map(activity => (
                        <button
                          type="button"
                          key={activity.id}
                          className="dash-activity-row"
                          onClick={() => navigate(activity.link)}
                        >
                          <i className={`dash-avatar ${activity.type}`}>
                            {activity.workflow ? renderWorkflowTypeIcon(activity.workflow) : <PipelineIcon />}
                          </i>
                          <span className="dash-activity-text">
                            <b>{activity.title}</b>
                            <span>{activity.description}</span>
                          </span>
                          <time>{formatDate(activity.timestamp)}</time>
                        </button>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* 최근 워크플로우 */}
            <div className="dash-card">
              <div className="dash-card-head">
                <h2>최근 워크플로우</h2>
                {recentWorkflows.length > 0 && (
                  <button type="button" className="dash-card-link" onClick={() => navigate('/workflows')}>전체 보기 →</button>
                )}
              </div>
              {recentWorkflows.length === 0 ? (
                <DashEmpty
                  icon={<WorkflowIcon />}
                  title="아직 만든 워크플로우가 없어요"
                  desc="마켓플레이스에서 설치한 노드를 조합해 첫 워크플로우를 만들어 보세요."
                  actions={
                    <>
                      <Button color="primary" size="md" onClick={handleCreateWorkflow} disabled={creating}>
                        {creating ? '생성 중...' : '+ 새 워크플로우'}
                      </Button>
                      <Button color="gray" appearance="outline" size="md" onClick={() => navigate('/templates')}>
                        템플릿 둘러보기
                      </Button>
                    </>
                  }
                />
              ) : (
                <div className="dash-table">
                  <div className="dash-tr dash-th">
                    <span>이름</span><span>상태</span><span>수정</span>
                  </div>
                  {recentWorkflows.map(workflow => (
                    <button
                      key={workflow.id}
                      type="button"
                      className="dash-tr"
                      onClick={() => navigate(`/workflows/${workflow.id}`)}
                    >
                      <span className="dash-name">
                        <i className="dash-avatar workflow">{renderWorkflowTypeIcon(workflow)}</i>
                        <span>{workflow.name}</span>
                      </span>
                      {renderStatus(WORKFLOW_STATUS[workflow.status], workflow.status)}
                      <span className="dash-muted">{formatDate(workflow.updatedAt)}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* 최근 지식 파이프라인 */}
            <div className="dash-card">
              <div className="dash-card-head">
                <h2>최근 지식 파이프라인</h2>
                {recentKnowledgeBases.length > 0 && (
                  <button type="button" className="dash-card-link" onClick={() => navigate('/knowledge-bases')}>전체 보기 →</button>
                )}
              </div>
              {recentKnowledgeBases.length === 0 ? (
                <DashEmpty
                  icon={<PipelineIcon />}
                  title="아직 만든 지식 파이프라인이 없어요"
                  desc="파일을 올리거나 DB를 연결해 챗봇이 참고할 지식을 만들어 보세요."
                  actions={
                    <Button color="primary" size="md" onClick={() => navigate('/knowledge-bases')}>
                      + 새 지식 파이프라인
                    </Button>
                  }
                />
              ) : (
                <div className="dash-table dash-table--kb">
                  <div className="dash-tr dash-th">
                    <span>이름</span><span>문서</span><span>상태</span><span>수정</span>
                  </div>
                  {recentKnowledgeBases.map(kb => (
                    <button
                      key={kb.id}
                      type="button"
                      className="dash-tr"
                      onClick={() => navigate(getKbManagePath(kb))}
                    >
                      <span className="dash-name">
                        <i className="dash-avatar kb"><PipelineIcon /></i>
                        <span>{kb.name}</span>
                      </span>
                      <span className="dash-num">{kb.documentCount || 0}</span>
                      {renderStatus(KB_STATUS[kb.status], kb.status, kb.status === 'syncing' || kb.status === 'indexing')}
                      <span className="dash-muted">{formatDate(kb.updatedAt)}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </section>

          {/* 빠른 실행 - 공통 item-card (왼쪽 아이콘 | 제목 · 설명 | 화살표) */}
          <section className="dash-actions">
            <button type="button" className="item-card dash-action primary" onClick={handleCreateWorkflow} disabled={creating}>
              <span className="item-card-icon"><PlusIcon /></span>
              <div className="item-card-body">
                <div className="item-card-title">{creating ? '생성 중...' : '새 워크플로우'}</div>
                <div className="item-card-desc">빈 캔버스에서 시작합니다</div>
              </div>
              <span className="dash-action-arrow">→</span>
            </button>
            <button type="button" className="item-card dash-action" onClick={() => navigate('/knowledge-bases')}>
              <span className="item-card-icon item-card-icon--primary"><PipelineIcon /></span>
              <div className="item-card-body">
                <div className="item-card-title">지식 파이프라인 관리</div>
                <div className="item-card-desc">문서 색인 상태를 확인합니다</div>
              </div>
              <span className="dash-action-arrow">→</span>
            </button>
            <button type="button" className="item-card dash-action" onClick={() => navigate('/templates')}>
              <span className="item-card-icon item-card-icon--primary"><TemplateIcon /></span>
              <div className="item-card-body">
                <div className="item-card-title">템플릿 둘러보기</div>
                <div className="item-card-desc">완성된 워크플로우를 가져옵니다</div>
              </div>
              <span className="dash-action-arrow">→</span>
            </button>
            <button type="button" className="item-card dash-action" onClick={() => navigate('/marketplace')}>
              <span className="item-card-icon item-card-icon--primary"><MarketplaceIcon /></span>
              <div className="item-card-body">
                <div className="item-card-title">마켓플레이스</div>
                <div className="item-card-desc">필요한 노드를 찾아 설치합니다</div>
              </div>
              <span className="dash-action-arrow">→</span>
            </button>
          </section>
        </div>
      </div>
    </>
  );
}

export default AdminDashboardPage;
